import React from "react";
import { ListChecks, Check, ArrowRight } from "@phosphor-icons/react";

// iter v28 r349 — Checklist "Cosa dire" con auto-spunta intelligente.
// iter v29 r243 — Regex con \w* per coprire declinazioni italiane.
// iter v29 r246 — User M1399 (3a iterazione): «la checklist auto-cambia
// schermata e mi disorienta, preferisco vedere tutto insieme come all'inizio
// ma scritto meglio, più chiaro, più visibile».
//
// Strategia FINALE:
//   • Tutte e 5 le voci sempre visibili, sempre nella stessa posizione
//     (niente più auto-rotazione di un'unica card "Adesso di':").
//   • Ogni voce è una mini-card con icona + titolo + esempio di una riga.
//   • Quelle già dette → check verde + line-through discreto, restano leggibili.
//   • Quelle ancora da dire → bianche, prominenti.
//   • Sticky in cima pagina (resta visibile durante registrazione).
const BLOCKS = [
  {
    key: "dove",
    icon: "📍",
    title: "Dove e quanto grande",
    items: "indirizzo · città · piano · mq",
    example: "Via Manzoni 41 a Bergamo, 3° piano, 90 mq",
    keywords: [
      /\b(via|viale|piazza|corso|largo|vicolo|strada|contrada)\b/i,
      /\b\d{1,3}\s*(m2|mq|metr[oi]?)\b/i,
      /\bpiano\b/i,
      /\bmetri\s+quadr/i,
    ],
  },
  {
    key: "dentro",
    icon: "🛏️",
    title: "Com'è dentro",
    items: "locali · bagni · cucina · balconi",
    example: "Trilocale, 2 bagni, cucina abitabile, balcone",
    keywords: [
      /\b(locale|locali|stanze|stanza|camera|camere|bilocale|trilocale|quadrilocale|monolocale)\b/i,
      /\b(bagno|bagni|servizio|servizi)\b/i,
      /\b(cucina|cucinino|cucinotto|cucinabile|cottura)\b/i,
      /\b(balcon\w*|terrazz\w*|loggia|logge|veranda)\b/i,
    ],
  },
  {
    key: "stato",
    icon: "🔨",
    title: "Stato e finiture",
    items: "nuovo / ristrutturato · infissi · riscaldamento",
    example: "Ristrutturato 2020, infissi nuovi, riscaldamento autonomo",
    keywords: [
      /\b(nuov[ao]|ristruttur\w*|rifatto|rifatti|ammodernat\w*|da ammodernare|ottim[ao] stato|buon[ao] stato|abitabile|da sistemare)\b/i,
      /\b(infiss\w*|finestr\w*|persian\w*|porte|porta|pavimento|pavimenti|parquet|marmo|piastrelle)\b/i,
      /\b(riscaldamento|autonom\w*|centralizzat\w*|caldaia|condizionator\w*|clima|climatizzator\w*)\b/i,
    ],
  },
  {
    key: "pregi",
    icon: "✨",
    title: "Pregi",
    items: "vista · ascensore · box · classe energetica",
    example: "Vista panoramica, esposizione sud, ascensore, box, classe B",
    keywords: [
      /\b(vista|panoram\w*|esposizion\w*|luminos\w*|espost[ao]|soleggi\w*)\b/i,
      /\b(ascensor\w*|box|posto auto|garage|cantin\w*|soffitt\w*|magazzin\w*|taverna)\b/i,
      /\b(classe\s+(?:energetica|[A-G])|energetic[ao]|APE)\b/i,
    ],
  },
  {
    key: "prezzo",
    icon: "💶",
    title: "Prezzo e disponibilità",
    items: "prezzo · spese · libero da",
    example: "300 mila, 50 euro di spese, libero subito",
    keywords: [
      /\b(prezzo|eur[oi]?|€|mila|migliaia|chied[ei])\b/i,
      /\b(spes[ei]|condominial\w*|condominio)\b/i,
      /\b(libero|disponibile|consegna|subito|fra|tra)\b/i,
    ],
  },
];

function detectCovered(transcript) {
  if (!transcript) return new Set();
  const covered = new Set();
  for (const b of BLOCKS) {
    if (b.keywords.some((re) => re.test(transcript))) {
      covered.add(b.key);
    }
  }
  return covered;
}

export default function CheckListPromemoria({ transcript = "" }) {
  const covered = detectCovered(transcript);
  const coveredCount = covered.size;
  const total = BLOCKS.length;
  const allDone = coveredCount === total;

  return (
    <div
      className="sticky z-20 mb-4 bg-[#FAF8F3] border border-[#E5E2D8] rounded-2xl overflow-hidden shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)]"
      style={{ top: "max(env(safe-area-inset-top, 0px), 8px)" }}
      data-testid="sopralluogo-checklist"
    >
      {/* ── Header con progress + dots ──────────────────────────────── */}
      <div className="px-4 py-2.5 border-b border-[#E5E2D8] flex items-center justify-between gap-2 bg-white/60">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks size={15} className="text-[#7C8B6E] shrink-0" weight="regular" />
          <div className="text-[11px] font-semibold text-[#1C1C1A] uppercase tracking-[0.18em]">
            Cosa dire
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex gap-1">
            {BLOCKS.map((b) => (
              <span
                key={b.key}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${covered.has(b.key) ? "bg-[#7C8B6E]" : "bg-[#D5DAC9]"}`}
                aria-hidden
              />
            ))}
          </div>
          <span className="text-[11px] font-semibold text-[#7C8B6E] tabular-nums" data-testid="checklist-progress">
            {coveredCount}/{total}
          </span>
        </div>
      </div>

      {/* ── Tutte le 5 voci, sempre visibili, sempre nello stesso ordine ── */}
      <div className="px-2.5 py-2.5">
        {allDone && (
          <div
            className="mb-2 px-3 py-2 rounded-lg bg-[#E8F0DD] border border-[#7C8B6E]/30 flex items-center gap-2"
            data-testid="checklist-all-done"
          >
            <div className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0">
              <Check size={12} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">
                Hai detto tutto · puoi fotografare
              </div>
            </div>
          </div>
        )}

        <ul className="space-y-1.5">
          {BLOCKS.map((b) => {
            const isCovered = covered.has(b.key);
            return (
              <li
                key={b.key}
                className={`flex items-start gap-2.5 px-2.5 py-1.5 rounded-lg transition-all ${
                  isCovered
                    ? "bg-[#E8F0DD]/40 opacity-75"
                    : "bg-white border border-[#EDEAE2]"
                }`}
                data-testid={`checklist-row-${b.key}`}
                data-covered={isCovered ? "true" : "false"}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[12px] ${
                    isCovered
                      ? "bg-[#7C8B6E] text-white"
                      : "bg-[#F1F4ED] text-[#7C8B6E]"
                  }`}
                >
                  {isCovered ? <Check size={12} weight="bold" /> : b.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-[12.5px] font-semibold leading-tight ${
                      isCovered
                        ? "text-[#5A6A4D] line-through decoration-[#7C8B6E]/40"
                        : "text-[#1C1C1A]"
                    }`}
                  >
                    {b.title}
                  </div>
                  {!isCovered && (
                    <div className="text-[10.5px] text-[#6B6A65] leading-snug mt-0.5">
                      {b.items}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* ── Tip correzione vocale ──────────────────────────────────── */}
        {!allDone && (
          <div className="mt-2.5 pt-2 border-t border-[#E5E2D8] flex items-start gap-1.5 text-[10.5px] text-[#9A968B] leading-snug px-1">
            <ArrowRight size={11} weight="bold" className="mt-0.5 text-[#7C8B6E] shrink-0" />
            <span>
              Parla con parole tue. Ogni 30 secondi Pronto sistema da sola nomi e refusi.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
